import { FC, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  MantineReactTable,
  MRT_ColumnDef,
  useMantineReactTable,
} from "mantine-react-table";
import { Text, Box, Button } from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import { toast } from "react-toastify";
import { RootState } from "../../../../store";
import useActiveNotes from "../../../../hooks/useActiveNotes";
import {
  ActiveNoteData,
  MerchantActiveNotes,
} from "../../../../services/getActiveNotes";
import { useStyles } from "./ActiveNotes.styles";
import { convertDate } from "@funded-here-interface/common/src/Utils/date";
import { downloadFile } from "@funded-here-interface/common/src/Utils/file";
import { FileType } from "@funded-here-interface/common/src/constant/enum";
import useDownloadActiveNotesCSV from "../../../../hooks/useDownloadActiveNotesCSV";

interface ActiveNoteRow extends ActiveNoteData {
  merchantName: string;
  merchantId: string;
}

const formatCurrency = (value: string | undefined): string => {
  const numValue = parseFloat(value || "0");
  return `$${numValue.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const ActiveNotes: FC = () => {
  const { classes } = useStyles();
  const token = useSelector((state: RootState) => state.auth.token);

  const { data, isLoading, isError } = useActiveNotes(token);
  const downloadMutation = useDownloadActiveNotesCSV();

  const handleExportCSV = () => {
    downloadMutation.mutate(
      { token },
      {
        onSuccess: (data: Blob) => {
          downloadFile(data, "Active_Notes", FileType.CSV);
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  const columns = useMemo<MRT_ColumnDef<ActiveNoteRow>[]>(
    () => [
      {
        accessorKey: "merchantName",
        header: "Merchant",
        size: 200,
        enableGrouping: true,
      },
      {
        accessorKey: "structuredNoteDisplayId",
        header: "Note ID",
        size: 120,
      },
      {
        accessorKey: "issuedAt",
        header: "Issue Date",
        size: 120,
        Cell: ({ cell }) => convertDate(cell.getValue<string>()),
      },
      {
        accessorKey: "maturityDate",
        header: "Maturity Date",
        size: 120,
        Cell: ({ cell }) => convertDate(cell.getValue<string>()),
      },
      {
        accessorKey: "tenure",
        header: "Tenure (Days)",
        size: 120,
        Cell: ({ cell }) => {
          const value = cell.getValue<number>();
          return value ? `${value} days` : "-";
        },
      },
      {
        accessorKey: "l2PlusE",
        header: "L2+E",
        size: 130,
        Cell: ({ cell }) => formatCurrency(cell.getValue<string>()),
      },
      {
        accessorKey: "outstandingAmount",
        header: "Outstanding Amount",
        size: 150,
        Cell: ({ cell }) => formatCurrency(cell.getValue<string>()),
      },
      {
        accessorKey: "repaidAmount",
        header: "Repaid Amount",
        size: 130,
        Cell: ({ cell }) => formatCurrency(cell.getValue<string>()),
      },
    ],
    []
  );

  // Transform merchant data into flat rows for the table
  const tableData = useMemo(() => {
    if (!data?.merchants) return [];

    return data.merchants.flatMap((merchant: MerchantActiveNotes) =>
      merchant.notes.map((note: ActiveNoteData) => ({
        ...note,
        merchantName: merchant.merchantName,
        merchantId: merchant.merchantOrgRoleId,
      }))
    );
  }, [data]);

  const table = useMantineReactTable({
    columns,
    data: tableData,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableSorting: true,
    enableGrouping: true,
    initialState: {
      density: "xs",
      grouping: ["merchantName"],
      expanded: true,
      pagination: { pageSize: 100, pageIndex: 0 },
    },
    state: {
      isLoading,
    },
    mantineTableProps: {
      withBorder: true,
      withColumnBorders: true,
    },
    mantineTableBodyCellProps: {
      style: {
        fontSize: "14px",
      },
    },
    mantineTableHeadCellProps: {
      style: {
        fontSize: "14px",
        fontWeight: 600,
      },
    },
  });

  if (isError) {
    return (
      <Box className={classes.container}>
        <Text color="red" size="lg">
          Error loading active notes data. Please try again later.
        </Text>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <Box>
          <Text size="xl" weight={700}>
            Active Notes by Merchant
          </Text>
          <Text size="sm" color="dimmed">
            Drag any of the drag handles (the six dots) next to a field header
            and drop it onto the blank row above the column headers to group the
            table by that field.
          </Text>
        </Box>
        <Button
          color="lightblue"
          onClick={handleExportCSV}
          leftIcon={<IconDownload />}
          variant="filled"
          loading={downloadMutation.isLoading}
        >
          Export CSV
        </Button>
      </Box>
      <MantineReactTable table={table} />
    </Box>
  );
};

export default ActiveNotes;
